import React, { Component } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { addFloorAreaFilter, removeFloorAreaFilter, fetchSearchResultsIfNotBusy } from '../actions/search'
import ThemedStyleSheet from 'react-with-styles/lib/ThemedStyleSheet'
import aphroditeInterface from 'react-with-styles-interface-aphrodite'
import DefaultTheme from 'rheostat/lib/themes/DefaultTheme'
ThemedStyleSheet.registerInterface( aphroditeInterface )
ThemedStyleSheet.registerTheme( DefaultTheme )
import { colors } from '../../search-common/settings'
import { debounce } from '../../modules/helper-functions'
import { translate } from '../../modules/helper-functions'
import { media } from '../../search-common/helpers'

const pitPoints = [ 0, 50, 250, 1000, 5000, 20000, 50000, 100000 ]

const Container = styled.div`
  width: 100%;
`

const InputContainer = styled.div`
  width: 100%;
  margin: 0 -5px;
  display: flex;
  align-items: center;
`

const Input = styled.input`
  margin: 0 5px;
  flex: 1 1 0;
  padding: 8px;
  text-align: center;
  background-color: transparent;
  border: 1px solid ${colors.grayTone3};
  color: ${colors.white};
  min-width: 0;
  min-height: 34px;
  font-size: 14px;
  ${media.tablet`
    flex-basis: 116px;
    min-height: 42px;
    font-size: 16px;
    max-width: 116px;
  `}
  ::-webkit-inner-spin-button{
    -webkit-appearance: none;
    margin: 0;
    -moz-appearance: textfield !important;
  }
  ::-webkit-outer-spin-button{
    -webkit-appearance: none;
    margin: 0;
    -moz-appearance: textfield !important;
  }
`

const Sup = styled.sup`
  color: ${colors.white};
  padding-left: 2px;
  top: -1em;
  user-select: none;
`

const Label = styled.label`
  display: block;
  font-weight: bold;
  margin-bottom: 5px;
  font-size: 14px;
`

/**
 * FloorAreasFilter component.
 */
class FloorAreasFilter extends Component {
  /**
   * @constructor
   * @param {any} props
   */
  constructor( props ) {
    super( props )

    this.state = {
      minValue: null,
      maxValue: null
    }

    this.handleSliderChange = this.handleSliderChange.bind( this )
    this.handleInputChange = this.handleInputChange.bind( this )
    this.getPitPoints = this.getPitPoints.bind( this )
  }

  /**
   * Function handles the slider state changes by
   * adding the new floor areas filter to redux state.
   *
   * @param {object} state
   * @return {object} State.
   */
  handleSliderChange( state ) {
    const { addFilter, doSearch } = this.props
    const minVal = state.values[0]
    const maxVal = state.values[state.values.length - 1]
    const min = this.getSmallestArea()
    const max = this.getLargestArea()
    addFilter({
      min: minVal >= min ? minVal : min,
      max: maxVal <= max ? maxVal : max
    })
    this.setState({
      minValue: String( minVal >= min ? minVal : min ),
      maxValue: String( maxVal <= max ? maxVal : max )
    })
    doSearch()
    return state
  }

  /**
   * Handles changes in the inputs.
   *
   */
  handleInputChange( min, max ) {
    const { addFilter, doSearch } = this.props
    this.setState({
      minValue: min,
      maxValue: max
    })

    if ( min === '' || max === '' ) {
      return
    }

    const minVal = parseInt( min, 10 )
    const maxVal = parseInt( max, 10 )
    if ( isNaN( minVal ) || isNaN( maxVal )) {
      return
    }

    const minimum = this.getSmallestArea()
    const maximum = this.getLargestArea()
    addFilter({
      min: minVal >= minimum ? minVal : minimum,
      max: maxVal <= maximum ? maxVal : maximum
    })
    doSearch()
  }

  /**
   * Function gets the smallest area from the
   * initial areas array. It assumes that the
   * initial areas are alreay ordered by the
   * area from smallest to largest.
   */
  getSmallestArea() {
    const { initialAreas } = this.props
    if ( initialAreas && initialAreas.length > 0 ) {
      return initialAreas[0].value
    } else {
      return 0
    }
  }

  /**
   * Function gets the largest area from the
   * initial areas array. It assumes that the
   * initial areas are alreay ordered by the
   * area from smallest to largest.
   */
  getLargestArea() {
    const { initialAreas } = this.props
    if ( initialAreas && initialAreas.length > 0 ) {
      return initialAreas[initialAreas.length - 1].value
    } else {
      return 0
    }
  }


  /**
   * Function filters the available pitpoints
   * for the slider component.
   */
  getPitPoints() {
    const max = this.getLargestArea()
    let points = pitPoints.filter(( point ) => point <= max )
    return points
  }

  /**
   * Render method.
   */
  render() {
    const { selectedItem, translations } = this.props
    const { minValue, maxValue } = this.state
    const min = this.getSmallestArea()
    const max = this.getLargestArea()
    const minVal = minValue === null ? ( selectedItem ? selectedItem.min : min ) : minValue
    const maxVal = maxValue === null ? ( selectedItem ? selectedItem.max : max ) : maxValue

    // Get the largest area count.

    return (
      <Container>
        <Label>{translate( translations, 'Floor area' )}</Label>
        <InputContainer>
          <Input
            type={'number'}
            id={'floor-area-min-input'}
            min={min}
            max={maxVal}
            value={minVal}
            onChange={( event ) => this.handleInputChange( event.target.value, maxVal )}
          />
          {' – '}
          <Input
            type={'number'}
            id={'floor-area-max-input'}
            min={minVal}
            max={max}
            value={maxVal}
            onChange={( event ) => this.handleInputChange( minVal, event.target.value )}
          />
          <Sup>{' m²'}</Sup>
        </InputContainer>
      </Container>
    )
  }
}

FloorAreasFilter.propTypes = {
  areas: PropTypes.array.isRequired,
  addFilter: PropTypes.func.isRequired,
  removeFilter: PropTypes.func.isRequired,
  doSearch: PropTypes.func.isRequired,
  selectedItem: PropTypes.object,
  initialAreas: PropTypes.array.isRequired,
  translations: PropTypes.object.isRequired
}

/**
 * Map the redux state to component props.
 *
 * @param {object} state
 * @return {object}
 */
const mapStateToProps = ( state ) => ({
  areas: state.search.facets.floor_areas,
  initialAreas: state.search.all_facets.floor_areas,
  selectedItem: state.search.filters.floor_area ? state.search.filters.floor_area : null,
  translations: state.translations,
})

/**
 * Add dispatch functions to components props.
 *
 * @param {function} dispatch
 * @return {object}
 */
const mapDispatchToProps = ( dispatch ) => {
  return {
    addFilter: ( area ) => {
      dispatch( addFloorAreaFilter( area ))
    },
    doSearch: debounce(() => {
      dispatch( fetchSearchResultsIfNotBusy())
    }, 500 ),
    removeFilter: () => {
      dispatch( removeFloorAreaFilter())
      dispatch( fetchSearchResultsIfNotBusy())
    }
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)( FloorAreasFilter )
